import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import {
  TrackingProvider,
  ConsoleProvider,
  EventPayloadMap,
  EventPriority,
  TrackingEvent,
  TrackingEventType,
  TrackingManager,
  TrackingMetadata,
} from '@ds24/tracking-core';

@Injectable({
  providedIn: 'root',
})
export class TrackingService implements OnDestroy {
  private readonly manager: TrackingManager;

  constructor() {
    this.manager = new TrackingManager();
    this.manager.registerProvider(new ConsoleProvider());
  }

  public registerProvider(provider: TrackingProvider): void {
    this.manager.registerProvider(provider);
  }

  public unregisterProvider(providerName: string): void {
    this.manager.unregisterProvider(providerName);
  }

  public addMetadata(metadata: Partial<TrackingMetadata>): void {
    this.manager.addMetadata(metadata);
  }

  public track<T extends TrackingEventType>(
    type: T,
    payload?: EventPayloadMap[T],
    priority: EventPriority = EventPriority.INFO
  ): void {
    this.manager.track(type, payload, priority);
  }

  /**
   * Tracks an event using navigator.sendBeacon for reliable delivery during page unload.
   * Routes to providers that support beacon delivery (e.g., PostHog).
   * Returns true if at least one provider successfully queued the beacon.
   *
   * Use this method for events that must be delivered even when the page is closing,
   * such as wizard close events triggered by beforeunload.
   *
   * @param type - The event type to track
   * @param payload - The event payload
   * @returns true if beacon was queued successfully, false otherwise
   */
  public trackAsBeacon<T extends TrackingEventType>(
    type: T,
    payload: EventPayloadMap[T]
  ): boolean {
    return this.manager.trackAsBeacon(type, payload);
  }

  public critical<T extends TrackingEventType>(name: T): void;
  public critical<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public critical<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    if (payload === undefined) {
      this.manager.critical(name);
    } else {
      this.manager.critical(name, payload);
    }
  }

  // Log-level based tracking aliases with overloads for optional payloads
  public error(error: Error): void {
    this.manager.error(error);
  }

  public warn<T extends TrackingEventType>(name: T): void;
  public warn<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public warn<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    if (payload === undefined) {
      this.manager.warn(name);
    } else {
      this.manager.warn(name, payload);
    }
  }

  public info<T extends TrackingEventType>(name: T): void;
  public info<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public info<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    if (payload === undefined) {
      this.manager.info(name);
    } else {
      this.manager.info(name, payload);
    }
  }

  public debug<T extends TrackingEventType>(name: T): void;
  public debug<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public debug<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    if (payload === undefined) {
      this.manager.debug(name);
    } else {
      this.manager.debug(name, payload);
    }
  }

  public trace<T extends TrackingEventType>(name: T): void;
  public trace<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public trace<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    if (payload === undefined) {
      this.manager.trace(name);
    } else {
      this.manager.trace(name, payload);
    }
  }

  // Event listener methods
  public onEventReceived(callback: (event: TrackingEvent) => void): () => void {
    return this.manager.onEventReceived(callback);
  }

  public offEventReceived(callback: (event: TrackingEvent) => void): void {
    this.manager.offEventReceived(callback);
  }

  public onEventProcessed(callback: (event: TrackingEvent) => void): () => void {
    return this.manager.onEventProcessed(callback);
  }

  public offEventProcessed(callback: (event: TrackingEvent) => void): void {
    this.manager.offEventProcessed(callback);
  }

  ngOnDestroy(): void {
    // Destroy the manager
    this.manager.destroy();
  }
}
